import { useEffect, type PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";

import { useNotifications } from "@/hooks/useNotifications";

// for more information see https://vite-pwa-org.netlify.app/frameworks/react.html

/**
 * This function will register a periodic sync check every hour, you can modify the interval as needed.
 */
function registerPeriodicSync(period: number, swUrl: string, r: ServiceWorkerRegistration) {
  if (period <= 0) return;

  setInterval(async () => {
    if ("onLine" in navigator && !navigator.onLine) return;

    const resp = await fetch(swUrl, {
      cache: "no-store",
      headers: {
        cache: "no-store",
        "cache-control": "no-cache",
      },
    });

    if (resp.status === 200) await r.update();
  }, period);
}

const PwaProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const notify = useNotifications();

  // ! check for updates every 60 minutes
  const period = 60 * 60 * 1000;

  const {
    offlineReady: [offlineReady, setOfflineReady],
    // needRefresh: [_needRefresh, setNeedRefresh], // * -> currently not needed due to autoUpdate
    // updateServiceWorker, // * -> currently not needed due to autoUpdate
  } = useRegisterSW({
    onRegisteredSW(swUrl, r) {
      if (period <= 0) return;
      if (r?.active?.state === "activated") {
        registerPeriodicSync(period, swUrl, r);
      } else if (r?.installing) {
        r.installing.addEventListener("statechange", (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === "activated") registerPeriodicSync(period, swUrl, r);
        });
      }
    },
  });

  useEffect(() => {
    if (offlineReady) {
      notify.info(t("app-offline-ready"), "", {
        onClose: () => {
          setOfflineReady(false);
        },
      });
    }
  }, [notify, offlineReady, setOfflineReady, t]);

  return children;
};

export default PwaProvider;
